// File: frontend/src/utils/momentReplay.ts
//
// Pitch replays for the score breakdown modal. Turns the moments payload
// (GET /stats/matches/{match}/players/{player}/moments) into animation data:
// one row per scoring moment, the colored marks for each moment, and the
// clip timings the renderer (components/dfs/MomentReplayPanel.tsx) plays.
//
// Ported from the owner-approved mockup (2026-09-29): LEAD / CARRY_LEAD
// timing, ball keyframes, colors (shots green, creation passes blue, Impact
// passes and carries yellow, lost ball red), teammates-only names.
//
// DOM-free and import-free on purpose so `node --test` can load it directly.

// --- Payload (backend/app/services/moments_service.py) ---------------------

/** [t, name, side (1 = the player's team), sx, sy, ex, ey, player_id, outcome, type] */
export type ClipEventRow = [
  t: number,
  name: string,
  side: number,
  sx: number | null,
  sy: number | null,
  ex: number | null,
  ey: number | null,
  playerId: number | null,
  outcome: string | null,
  type: string | null,
];

export interface MomentScore {
  score_type: string;
  points: number;
}

export interface MomentResult {
  event_id: string;
  event_name: string | null;
  outcome: string | null;
  is_goal: boolean;
  /** Only ever a teammate; opponents are never named. */
  shooter_id: number | null;
  /** StatsBomb shot type of the created shot ('penalty', 'free-kick', 'open-play', ...). */
  shot_type?: string | null;
  /** True only when the server has a complete, uninterrupted foul-to-shot sequence. */
  foul_played_on?: boolean;
  period: number | null;
  seconds: number | null;
}

export interface PlayerMoment {
  event_id: string;
  period: number;
  minute: number;
  seconds: number | null;
  points: number;
  scores: MomentScore[];
  via: string | null;
  result: MomentResult | null;
  clip_ids: string[];
}

export interface MomentClip {
  id: string;
  period: number;
  start: number;
  end: number;
  events: ClipEventRow[];
}

export interface PlayerMatchMoments {
  match: {
    match_id: number;
    home_team: string | null;
    away_team: string | null;
    home_team_id: number | null;
    away_team_id: number | null;
    kickoff_utc: string | null;
    status: string | null;
    team_id: number | null;
    source: 'stored' | 'snapshot' | 'none';
    scores_final: boolean;
  };
  player: { id: number; nickname: string | null; display_name: string };
  moments: PlayerMoment[];
  clips: MomentClip[];
  /** Teammates only; render through formatPlayerDisplayName. */
  names: Record<string, { nickname: string | null; name: string | null }>;
  total: number;
}

export const formatPoints = (n: number): string => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));
export const signedPoints = (n: number): string => `${n >= 0 ? '+' : ''}${formatPoints(n)}`;

// --- Rows ------------------------------------------------------------------

/** The fields the modal's score rows carry (ApiFantasyScore). */
export interface ScoreRowInput {
  id: number;
  event_id?: string | null;
  score_type?: string | null;
  points: number;
  period: number;
  minute: number;
  event_index?: number | null;
}

export interface MomentRowScore {
  type: string;
  points: number;
  via: string | null;
}

export interface MomentRow {
  key: string;
  period: number;
  minute: number;
  points: number;
  scores: MomentRowScore[];
  resultText: string | null;
}

const SCORE_TYPE_ORDER = ['Goal', 'ShotOnTarget', 'Shot', 'GCA', 'SCA', 'ProgCarry', 'ProgPass', 'Dispossessed'];
const CREATION_TYPES = new Set(['SCA', 'GCA']);

const typeOrder = (type: string): number => {
  const i = SCORE_TYPE_ORDER.indexOf(type);
  return i === -1 ? SCORE_TYPE_ORDER.length : i;
};

/** Why an SCA/GCA counts as creation, in the Film Room's words. */
export function viaLabel(via: string | null | undefined, scoreType: string): string | null {
  if (!CREATION_TYPES.has(scoreType)) return null;
  switch (via) {
    case 'pass': return 'Pass';
    case 'foul-won': return 'Foul Won';
    case 'dribble': return 'Dribble';
    case 'shot': return scoreType === 'GCA' ? 'Shot Leading to Goal' : 'Shot Leading to Shot';
    default: return null;
  }
}

const OUTCOME_TEXT: Record<string, string> = {
  goal: 'scored',
  saved: 'saved',
  'saved-to-post': 'saved',
  'saved-off-t': 'saved',
  blocked: 'blocked',
  post: 'hit the post',
  'off-t': 'off target',
  wayward: 'off target',
};

const article = (name: string) => (/^[aeiouáéíóúàèìòùâêîôûäëïöüøæœ]/i.test(name) ? 'an' : 'a');

/** "Created a Bruno Fernandes shot, hit the post" for a creation moment's result. */
export function resultText(
  result: MomentResult | null | undefined,
  playerId: number,
  names: Record<string, string>,
  via: string | null = null,
): string | null {
  if (!result || !result.event_name) return null;
  if (result.event_name === 'own-goal-for') return 'Forced an own goal';
  const outcome = result.outcome ? (OUTCOME_TEXT[result.outcome] ?? result.outcome.replace(/-/g, ' ')) : null;
  const withOutcome = (text: string) => (outcome && !result.is_goal ? `${text}, ${outcome}` : text);
  const self = result.shooter_id === playerId;
  const shooter = result.shooter_id != null ? names[String(result.shooter_id)] : undefined;
  if (via === 'foul-won' && result.shot_type === 'penalty') {
    if (result.is_goal) return self ? 'Won a penalty and scored it' : shooter ? `Won a penalty, ${shooter} scored it` : 'Won a penalty, scored';
    return withOutcome(self ? 'Won a penalty and took it' : 'Won a penalty');
  }
  if (via === 'foul-won' && self && result.shot_type === 'free-kick') {
    return result.is_goal ? 'Won a free kick and scored it' : withOutcome('Won a free kick and took it');
  }
  if (via === 'foul-won' && self && result.shot_type === 'open-play') {
    const lead = result.foul_played_on === true ? 'Fouled, played on, and' : 'Fouled, then';
    if (result.is_goal) return `${lead} scored`;
    if (outcome === 'off target') return `${lead} shot off target`;
    if (outcome === 'hit the post') return `${lead} hit the post`;
    if (outcome === 'saved' || outcome === 'blocked') return `${lead} had a shot ${outcome}`;
    return withOutcome(`${lead} took a shot`);
  }
  if (result.shot_type === 'penalty') {
    // e.g. a delivery that ended in a handball: the penalty came later, often after VAR.
    if (result.is_goal) return self ? 'Led to a penalty, then scored it' : shooter ? `Led to a penalty, ${shooter} scored it` : 'Led to a penalty, scored';
    return withOutcome(self ? 'Led to a penalty, then took it' : 'Led to a penalty');
  }
  if (self) return result.is_goal ? 'Followed up and scored' : withOutcome('Followed up with a shot');
  if (result.is_goal) return shooter ? `Set up ${article(shooter)} ${shooter} goal` : 'Set up a goal';
  return withOutcome(shooter ? `Created ${article(shooter)} ${shooter} shot` : 'Created a shot');
}

/**
 * One row per scoring moment (every score sharing a StatsBomb event), in match
 * order, biggest score first inside a row. Rows come from the scores the modal
 * was opened with, so they always match the points the user tapped; the
 * moments payload only adds the creation "via" and the result line.
 * `names` are display names (already through formatPlayerDisplayName).
 */
export function buildMomentRows(
  scores: ScoreRowInput[],
  moments: PlayerMatchMoments | null,
  names: Record<string, string> = {},
): MomentRow[] {
  const byEvent = new Map<string, PlayerMoment>();
  moments?.moments.forEach(m => byEvent.set(m.event_id, m));
  const groups = new Map<string, { row: MomentRow; order: number }>();
  scores.forEach(score => {
    const key = score.event_id || `score-${score.id}`;
    let group = groups.get(key);
    if (!group) {
      const order = score.event_index ?? Number.MAX_SAFE_INTEGER;
      group = { row: { key, period: score.period, minute: score.minute, points: 0, scores: [], resultText: null }, order };
      groups.set(key, group);
    }
    const type = score.score_type || 'Unknown';
    const moment = byEvent.get(key);
    group.row.scores.push({ type, points: score.points || 0, via: viaLabel(moment?.via, type) });
    group.row.points += score.points || 0;
  });
  const rows = [...groups.values()];
  rows.forEach(({ row }) => {
    row.points = Math.round(row.points * 100) / 100;
    row.scores.sort((a, b) => b.points - a.points || typeOrder(a.type) - typeOrder(b.type));
    const moment = byEvent.get(row.key);
    if (moment && moments && row.scores.some(s => CREATION_TYPES.has(s.type))) {
      row.resultText = resultText(moment.result, moments.player.id, names, moment.via);
    }
  });
  rows.sort((a, b) => a.row.period - b.row.period || a.row.minute - b.row.minute || a.order - b.order);
  return rows.map(({ row }) => row);
}

// --- Clips -----------------------------------------------------------------

export type Point = [number, number];

export interface ReplayEvent {
  k: number;
  t: number;
  n: string;
  side: number;
  s: Point;
  e: Point | null;
  pid: number | null;
  o: string | null;
  ty: string | null;
  arrive?: number;
  /** Logged but not drawn (attempted block, keeper conceding): never the player on the ball. */
  ghost?: boolean;
  /** A defender's contact during someone else's clean carry: drawn, but he never has the ball. */
  inCarry?: boolean;
}

export interface Keyframe {
  t: number;
  x: number;
  y: number;
}

export interface Track {
  pid: number;
  side: number;
  kf: Keyframe[];
}

export interface ContextLine {
  t: number;
  s: Point;
  e: Point;
  side: number;
  shot: boolean;
}

export interface PreparedClip {
  id: string;
  period: number;
  windowEnd: number;
  ev: ReplayEvent[];
  kf: Keyframe[];
  tracks: Track[];
  lines: ContextLine[];
  dataStart: number;
  /** Stoppages (ball out, then a restart) that playback jumps over: [from, to]. */
  cuts: [number, number][];
}

export const LEAD = 5;            // seconds shown before a scoring action
export const CARRY_LEAD = 2.5;    // ...or before the carry that led to it begins
export const END_TAIL = 3;        // seconds shown after the action (or the shot it created)
export const GOAL_END_TAIL = 4.5;
export const GAIN = ['ball-receipt', 'ball-recovery', 'interception', 'dribble'];
// Where the scoring engine opens a carry (workers/event_processing/calculations.py);
// a dribble mid-carry does not restart it.
const CARRY_START = ['ball-receipt', 'ball-recovery', 'interception'];

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

// During a carry: the carrier's own touches, and defenders' contact that doesn't take the ball.
const CARRY_TOUCH = new Set(['dribble', 'duel']);
// A foul on the carrier is contact too: it ends the carry at his 'foul-won'...
const CARRY_CONTACT = new Set(['duel', 'dribble-past', 'foul-committed']);

// ...unless play went on (owner call 2026-09-30): the fouled player's own
// open-play touch, not a restart, within 10 s of the foul is advantage. The
// scoring engine's canonical recalc carries through such a foul (ADVANTAGE_* in
// workers/event_processing/calculations.py), so the replay draws neither foul
// row and the carry runs straight on. Live scores still end the carry at the
// foul until the recalc, so a foul a carry is still scored on stays where it is.
const ADVANTAGE_WINDOW = 10;
const ADVANTAGE_IGNORED = new Set(['foul-committed', 'bad-behaviour', 'substitution', 'player-off', 'player-on', 'tactical-shift']);
const ADVANTAGE_PLAY_ON = new Set(['pass', 'shot', 'dribble', 'miscontrol', 'dispossessed', 'foul-won']);

/** Both rows (foul-won and the defender's foul-committed) of every foul played under advantage. */
function advantageFouls(ev: ReplayEvent[], carryEnds: number[]): Set<number> {
  const out = new Set<number>();
  ev.forEach((foul, i) => {
    if (foul.n !== 'foul-won' || foul.pid == null || carryEnds.some(at => Math.abs(at - foul.t) < 0.005)) return;
    const next = ev.slice(i + 1).find(e => !ADVANTAGE_IGNORED.has(e.n));
    if (!next || next.pid !== foul.pid || !ADVANTAGE_PLAY_ON.has(next.n) || RESTARTS.has(next.ty ?? '')) return;
    if (next.t < foul.t || next.t - foul.t > ADVANTAGE_WINDOW) return;
    out.add(foul.k);
    ev.forEach(e => {
      if (e.n === 'foul-committed' && e.side !== foul.side && Math.abs(e.t - foul.t) < 0.01) out.add(e.k);
    });
  });
  return out;
}

interface CleanCarry {
  start: ReplayEvent;
  end: ReplayEvent;
  inner: ReplayEvent[];
}

/**
 * Carries run straight (owner call 2026-09-30): from winning the ball to
 * releasing it, nothing but the carrier's own dribbles/duels and defenders'
 * tackle attempts. Someone else touching the ball mid-run is not a clean carry.
 */
function cleanCarries(ev: ReplayEvent[], ignore: Set<number>): CleanCarry[] {
  const out: CleanCarry[] = [];
  ev.forEach((start, i) => {
    if (start.pid == null || !CARRY_START.includes(start.n)) return;
    const inner: ReplayEvent[] = [];
    for (let j = i + 1; j < ev.length; j++) {
      const e = ev[j];
      const ownTouch = e.pid === start.pid && CARRY_TOUCH.has(e.n);
      const contact = e.pid !== start.pid && e.side !== start.side && CARRY_CONTACT.has(e.n);
      if (ownTouch || contact || ignore.has(e.k)) { inner.push(e); continue; }
      const released = e.pid === start.pid || (e.pid == null && e.n === 'out');
      if (released && e.t - start.t > 0.3) out.push({ start, end: e, inner });
      return;
    }
  });
  return out;
}

/** Every team attacks left to right in StatsBomb; draw the player's team that way. */
const flip = (x: number, y: number, side: number): Point => (side === 1 ? [x, y] : [120 - x, 80 - y]);

/**
 * Where the clip's Impact Carries are scored. Only a carry pins its end: a
 * creation credited on an advantage foul still lets the carry run through it.
 */
export function carryEndTimes(moments: PlayerMoment[], clipId: string): number[] {
  return moments
    .filter(m => m.seconds != null && m.clip_ids.includes(clipId) && m.scores.some(s => s.score_type === 'ProgCarry'))
    .map(m => m.seconds as number);
}

/** carryEnds: carryEndTimes() for this clip (a foul a carry is scored on never counts as advantage). */
export function prepareClip(clip: MomentClip, carryEnds: number[] = []): PreparedClip | null {
  const ev: ReplayEvent[] = [];
  clip.events.forEach(r => {
    const [t, n, side, sx, sy, ex, ey, pid, o, ty] = r;
    if (sx == null || sy == null) return;
    ev.push({
      k: ev.length, t, n, side,
      s: flip(sx, sy, side),
      e: ex == null || ey == null ? null : flip(ex, ey, side),
      pid, o, ty,
    });
  });
  if (!ev.length) return null;

  // A 'block' counts only if it stopped the ball (owner call 2026-09-30): an
  // attempt on a shot that scored, was saved or went wide, or on a pass that
  // still arrived, gets no dot and does not pull the ball.
  const attemptedBlock = new Set<number>();
  let lastStrike: ReplayEvent | null = null;
  for (const e of ev) {
    if (e.n === 'shot' || e.n === 'pass') { lastStrike = e; continue; }
    if (!lastStrike) continue;
    if (e.n === 'block') {
      const stopped = lastStrike.n === 'shot' ? lastStrike.o === 'blocked' : lastStrike.o !== 'complete';
      if (!stopped) attemptedBlock.add(e.k);
    } else if (e.n !== 'goal-keeper' && e.n !== 'dribble-past') {
      lastStrike = null; // play has moved on from the strike
    }
  }

  const advantage = advantageFouls(ev, carryEnds);

  // Carries run straight: the ball skips everything logged inside a clean carry.
  const carries = cleanCarries(ev, new Set([...attemptedBlock, ...advantage]));
  const insideCarry = new Set<number>();
  const carrierTouch = new Set<number>();
  carries.forEach(c => c.inner.forEach(e => {
    insideCarry.add(e.k);
    if (e.pid === c.start.pid) carrierTouch.add(e.k);
    else e.inCarry = true;
  }));

  // Ball keyframes: each event's location at its time; passes and shots also
  // land at their end point, arriving with the next touch when it is there.
  // Where a keeper stood when a shot went past untouched ("shot faced", "goal
  // conceded") is not where the ball went; the keeper's dot still shows.
  const keeperUntouched = (e: ReplayEvent) =>
    e.n === 'goal-keeper' && (e.ty === 'shot-faced' || e.ty === 'goal-conceded' || e.ty === 'penalty-conceded');
  const ballEv = ev.filter(e => e.n !== 'dribble-past' && !attemptedBlock.has(e.k) && !advantage.has(e.k) && !keeperUntouched(e) && !insideCarry.has(e.k));
  const kf: Keyframe[] = [];
  let skipUntil = -Infinity;
  // The keeper's "goal conceded" row after a goal: no dot for it (owner call
  // 2026-09-30, a keeper appearing in the net after the ball is confusing).
  const concededKeeper = new Set<number>();
  ballEv.forEach((e, i) => {
    if (e.t <= skipUntil && (e.n === 'block' || e.n === 'goal-keeper')) {
      if (e.n === 'goal-keeper') concededKeeper.add(e.k);
      return;
    }
    kf.push({ t: e.t, x: e.s[0], y: e.s[1] });
    if (e.n === 'own-goal-against') {
      // No end point is logged for an own goal: carry the ball on into the net.
      kf.push({ t: e.t + 0.45, x: e.s[0] > 60 ? 120 : 0, y: clamp(e.s[1], 36.5, 43.5) });
      skipUntil = e.t + 3;
      return;
    }
    if (!e.e) return;
    let next: ReplayEvent | null = null;
    for (let j = i + 1; j < ballEv.length; j++) {
      if (ballEv[j].t > e.t + 0.01) { next = ballEv[j]; break; }
    }
    const d = Math.hypot(e.e[0] - e.s[0], e.e[1] - e.s[1]);
    let arrive: number;
    if (e.n === 'shot' && e.o === 'goal') {
      arrive = e.t + clamp(d / 35, 0.35, 1.2);
      skipUntil = e.t + 3;
    } else {
      arrive = e.t + clamp(d / 28, 0.3, 2.5);
      if (next) {
        const gap = Math.hypot(next.s[0] - e.e[0], next.s[1] - e.e[1]);
        arrive = gap < 3 ? next.t : Math.min(arrive, next.t);
      }
    }
    if (arrive <= e.t) arrive = e.t + 0.25;
    e.arrive = arrive;
    kf.push({ t: arrive, x: e.e[0], y: e.e[1] });
  });
  kf.sort((a, b) => a.t - b.t);

  const tracks = new Map<number, Track>();
  ev.forEach(e => {
    const conceded = e.n === 'goal-keeper' && (e.ty === 'goal-conceded' || e.ty === 'penalty-conceded');
    if (conceded || concededKeeper.has(e.k) || attemptedBlock.has(e.k) || advantage.has(e.k)) e.ghost = true;
    // A defender beaten on a dribble is never drawn (owner call 2026-09-30).
    if (e.pid == null || e.ghost || e.n === 'dribble-past' || carrierTouch.has(e.k)) return;
    let track = tracks.get(e.pid);
    if (!track) { track = { pid: e.pid, side: e.side, kf: [] }; tracks.set(e.pid, track); }
    track.kf.push({ t: e.t, x: e.s[0], y: e.s[1] });
  });
  // The carrier's dot rides the carry line, with a point every 2 s so a long run never blinks out.
  carries.forEach(({ start, end }) => {
    const track = tracks.get(start.pid as number);
    if (!track) return;
    for (let t = start.t + 2; t < end.t - 0.5; t += 2) {
      const u = (t - start.t) / (end.t - start.t);
      track.kf.push({ t, x: start.s[0] + (end.s[0] - start.s[0]) * u, y: start.s[1] + (end.s[1] - start.s[1]) * u });
    }
  });
  tracks.forEach(track => track.kf.sort((a, b) => a.t - b.t));

  const lines: ContextLine[] = [];
  ev.forEach(e => {
    if (e.e && (e.n === 'pass' || e.n === 'shot')) lines.push({ t: e.t, s: e.s, e: e.e, side: e.side, shot: e.n === 'shot' });
  });

  return {
    id: clip.id,
    period: clip.period,
    windowEnd: clip.end,
    ev,
    kf,
    tracks: [...tracks.values()],
    lines,
    dataStart: ev[0].t - 0.6,
    cuts: stoppageCuts(ev, kf),
  };
}

// --- Moments on the pitch --------------------------------------------------

export type MarkKind = 'shot' | 'create' | 'impact' | 'lost' | 'create-dot';

/** A colored scoring mark, drawn along with the ball from t0 to t1. */
export interface Mark {
  kind: MarkKind;
  t0: number;
  t1: number;
  s: Point;
  e: Point | null;
}

export interface ReplayMoment {
  key: string;
  at: number;
  period: number;
  points: number;
  scoreTypes: string[];
  via: string | null;
  result: MomentResult | null;
  clipIds: string[];
}

/** The player's own event the moment is scored on. A carry can end on an 'out' with no player. */
export function actionEvent(clip: PreparedClip, m: ReplayMoment, playerId: number): ReplayEvent | null {
  const atTime = clip.ev.filter(e => Math.abs(e.t - m.at) < 0.005);
  return atTime.find(e => e.pid === playerId && e.n !== 'ball-receipt')
    ?? atTime.find(e => e.pid === playerId)
    ?? atTime.find(e => e.n !== 'ball-receipt')
    ?? atTime[0]
    ?? null;
}

function carryStart(clip: PreparedClip, e0: ReplayEvent, playerId: number): ReplayEvent | null {
  let start: ReplayEvent | null = null;
  for (const e of clip.ev) {
    if (e.k >= e0.k) break;
    if (e.pid === playerId && CARRY_START.includes(e.n)) start = e;
  }
  return start;
}

export function momentMarks(clip: PreparedClip, m: ReplayMoment, playerId: number): Mark[] {
  const e0 = actionEvent(clip, m, playerId);
  if (!e0) return [];
  const marks: Mark[] = [];
  if (m.scoreTypes.includes('ProgCarry')) {
    const st = carryStart(clip, e0, playerId);
    if (st) marks.push({ kind: 'impact', t0: st.t, t1: e0.t, s: st.s, e: e0.s });
  }
  const creation = m.scoreTypes.some(t => CREATION_TYPES.has(t));
  if (e0.n === 'pass' && e0.e) {
    // A pass that is both creation and Impact shows blue (owner call).
    const createdByPass = creation && m.via === 'pass';
    if (createdByPass || m.scoreTypes.includes('ProgPass')) {
      marks.push({ kind: createdByPass ? 'create' : 'impact', t0: e0.t, t1: e0.arrive ?? e0.t, s: e0.s, e: e0.e });
    }
  }
  if (e0.n === 'shot' && e0.e) marks.push({ kind: 'shot', t0: e0.t, t1: e0.arrive ?? e0.t, s: e0.s, e: e0.e });
  if (e0.n === 'dispossessed') marks.push({ kind: 'lost', t0: e0.t, t1: e0.t, s: e0.s, e: null });
  if (creation && (m.via === 'foul-won' || m.via === 'dribble')) marks.push({ kind: 'create-dot', t0: e0.t, t1: e0.t, s: e0.s, e: null });
  return marks;
}

const RESTARTS = new Set(['throw-in', 'goal-kick', 'corner', 'free-kick', 'kick-off', 'penalty']);

/**
 * Dead time inside a clip (more than 3 s before a corner, throw-in, free
 * kick...) is cut like a broadcast: hold 1.2 s after play stops, then jump to
 * 0.6 s before the restart. The ball holds where play stopped and reappears at
 * the restart spot instead of gliding across the pitch. Mutates kf.
 */
function stoppageCuts(ev: ReplayEvent[], kf: Keyframe[]): [number, number][] {
  const cuts: [number, number][] = [];
  for (let i = 1; i < ev.length; i++) {
    const prev = ev[i - 1];
    const next = ev[i];
    if (next.t - prev.t <= 3 || !RESTARTS.has(next.ty ?? '')) continue;
    const settled = Math.max(prev.t, prev.arrive ?? prev.t);
    const from = settled + 1.2;
    const to = next.t - 0.6;
    if (to - from < 0.5) continue;
    const [hx, hy] = ballAt(kf, settled);
    kf.push({ t: from, x: hx, y: hy }, { t: to, x: next.s[0], y: next.s[1] });
    cuts.push([from, to]);
  }
  kf.sort((a, b) => a.t - b.t);
  return cuts;
}

/** Playback time after dt, jumping over a cut stoppage. */
export function advanceTime(clip: PreparedClip, T: number, dt: number): number {
  const next = T + dt;
  for (const [from, to] of clip.cuts) if (next > from && next < to) return to;
  return next;
}

/** The ball's trail never reaches back across a cut (no streak over the jump). */
export function trailStart(clip: PreparedClip, T: number): number {
  let start = -Infinity;
  for (const [, to] of clip.cuts) if (to <= T + 1e-9) start = Math.max(start, to);
  return start;
}

/**
 * A lead-in that would open on a stoppage (the ball out of play for more than
 * 3 s before a corner, throw-in, free kick...) starts just before the restart
 * instead. Gaps during a dribble or a long ball are play and are kept.
 */
export function skipStoppage(clip: PreparedClip, start: number, until: number): number {
  let begin = start;
  let prevT = start;
  for (const e of clip.ev) {
    if (e.t <= start) continue;
    if (e.t > until) break;
    if (e.t - prevT > 3 && RESTARTS.has(e.ty ?? '')) begin = Math.max(begin, e.t - 0.6);
    prevT = e.t;
  }
  return begin;
}

/** Where a moment's replay starts: a few seconds before the action, or before the carry begins. */
export function momentAnchor(clip: PreparedClip, m: ReplayMoment, playerId: number): number {
  const e0 = actionEvent(clip, m, playerId);
  if (e0 && m.scoreTypes.includes('ProgCarry')) {
    const st = carryStart(clip, e0, playerId);
    if (st) return skipStoppage(clip, st.t - CARRY_LEAD, m.at);
  }
  return skipStoppage(clip, m.at - LEAD, m.at);
}

/** Opponent events are drawn only inside [oppFrom, oppUntil]. */
export interface OpponentWindow {
  oppFrom: number;
  oppUntil: number;
}

export interface Pop extends OpponentWindow {
  key: string;
  at: number;
  anchor: number;
  end: number;
  marks: Mark[];
}

/** A created shot far enough away to get its own clip. */
export interface Continuation extends OpponentWindow {
  key: string;
  at: number;
  start: number;
  end: number;
  caption: string;
}

export interface ReplayItem extends OpponentWindow {
  clip: PreparedClip;
  start: number;
  end: number;
  pops: Pop[];
  conts: Continuation[];
}

const OPPONENT_BALL = new Set(['pass', 'ball-receipt', 'ball-recovery', 'interception', 'clearance', 'dribble', 'shot']);

/** The opponent has the ball (not a tackle, block or foul on our player; not a keeper watching it go wide). */
function opponentHasBall(e: ReplayEvent): boolean {
  if (e.side === 1 || e.ghost) return false;
  if (e.n === 'goal-keeper') return !['shot-faced', 'goal-conceded', 'penalty-conceded'].includes(e.ty ?? '');
  return OPPONENT_BALL.has(e.n);
}

/**
 * Keep a replay to our side's possession (owner call 2026-09-30): it opens no
 * earlier than 1 s before the opponent's last touch before we won the ball, and
 * closes 1.2 s after their first touch once the moment has played out (never
 * before the payoff). Their passing outside that is not drawn.
 */
export function possessionTrim(
  clip: PreparedClip, at: number, payoff: number, start: number, end: number,
): { start: number; end: number } & OpponentWindow {
  let before: ReplayEvent | null = null;
  let after: ReplayEvent | null = null;
  for (const e of clip.ev) {
    if (!opponentHasBall(e)) continue;
    if (e.t < at - 0.005) before = e;
    else if (!after && e.t >= payoff - 0.05) after = e;
  }
  return {
    start: before ? Math.max(start, before.t - 1) : start,
    end: after ? Math.min(end, Math.max(payoff + 0.8, after.t + 1.2)) : end,
    oppFrom: before ? before.t - 0.01 : -Infinity,
    oppUntil: after ? after.t + 0.01 : Infinity,
  };
}

const inClip = (clip: PreparedClip, period: number | null, t: number | null): boolean =>
  t != null && period === clip.period && t <= clip.windowEnd;

/** Clips to play, in match order, with the moments that score in each. */
export function buildReplayItems(
  moments: ReplayMoment[],
  clips: Map<string, PreparedClip>,
  playerId: number,
): ReplayItem[] {
  const items = new Map<string, ReplayItem>();
  const itemFor = (clip: PreparedClip) => {
    let item = items.get(clip.id);
    if (!item) { item = { clip, start: Infinity, end: -Infinity, pops: [], conts: [], oppFrom: Infinity, oppUntil: -Infinity }; items.set(clip.id, item); }
    return item;
  };
  moments.forEach(m => {
    const clip = clips.get(m.clipIds[0]);
    if (!clip) return;
    const result = m.result;
    const ownClip = m.clipIds.length < 2 && inClip(clip, result?.period ?? null, result?.seconds ?? null);
    const goal = m.scoreTypes.includes('Goal') || (ownClip && !!result?.is_goal);
    const e0 = actionEvent(clip, m, playerId);
    const last = Math.max(m.at, e0?.arrive ?? m.at, ownClip ? (result?.seconds as number) : m.at);
    const trim = possessionTrim(
      clip, m.at, last, Math.max(clip.dataStart, momentAnchor(clip, m, playerId)), last + (goal ? GOAL_END_TAIL : END_TAIL),
    );
    const pop: Pop = {
      key: m.key,
      at: m.at,
      anchor: trim.start,
      end: trim.end,
      marks: momentMarks(clip, m, playerId),
      oppFrom: trim.oppFrom,
      oppUntil: trim.oppUntil,
    };
    const item = itemFor(clip);
    item.pops.push(pop);
    item.start = Math.min(item.start, pop.anchor);
    item.end = Math.max(item.end, pop.end);
    item.oppFrom = Math.min(item.oppFrom, pop.oppFrom);
    item.oppUntil = Math.max(item.oppUntil, pop.oppUntil);

    const second = m.clipIds[1] ? clips.get(m.clipIds[1]) : undefined;
    if (second && result?.seconds != null) {
      const at = result.seconds;
      const contTrim = possessionTrim(
        second, at, at, Math.max(second.dataStart, skipStoppage(second, at - LEAD, at)), at + (result.is_goal ? GOAL_END_TAIL : END_TAIL),
      );
      const cont: Continuation = {
        key: m.key,
        at,
        start: contTrim.start,
        end: contTrim.end,
        oppFrom: contTrim.oppFrom,
        oppUntil: contTrim.oppUntil,
        // No time gap in the caption: a VAR check can put minutes between them.
        caption: result.shot_type === 'penalty' ? 'The penalty it led to' : `The ${result.is_goal ? 'goal' : 'shot'} it created`,
      };
      const contItem = itemFor(second);
      contItem.conts.push(cont);
      contItem.start = Math.min(contItem.start, cont.start);
      contItem.end = Math.max(contItem.end, cont.end);
      contItem.oppFrom = Math.min(contItem.oppFrom, cont.oppFrom);
      contItem.oppUntil = Math.max(contItem.oppUntil, cont.oppUntil);
    }
  });
  const list = [...items.values()];
  list.forEach(item => item.pops.sort((a, b) => a.at - b.at));
  return list.sort((a, b) => a.clip.period - b.clip.period || a.start - b.start);
}

/**
 * The replayable moments: rows the modal shows that the payload places in a
 * clip. Points come from the rows, so each moment adds exactly what its row
 * shows to Play all's running total.
 */
export function replayMoments(payload: PlayerMatchMoments, rows: MomentRow[]): ReplayMoment[] {
  const rowPoints = new Map(rows.map(r => [r.key, r.points]));
  return payload.moments
    .filter(m => m.seconds != null && m.clip_ids.length > 0 && rowPoints.has(m.event_id))
    .map(m => ({
      key: m.event_id,
      at: m.seconds as number,
      period: m.period,
      points: rowPoints.get(m.event_id) as number,
      scoreTypes: m.scores.map(s => s.score_type),
      via: m.via,
      result: m.result,
      clipIds: m.clip_ids,
    }));
}

// --- Playback queue --------------------------------------------------------

export interface QueueEntry {
  item: ReplayItem;
  startT: number;
  endT: number;
  /** Single-moment playback: the one moment that scores (others stay quiet). */
  focus: string | null;
  caption: string | null;
  /** Playback speed: 1, or faster so the replay fits REPLAY_LIMIT_SECONDS. */
  rate: number;
  /** Opponent lines and dots outside this window are not drawn. */
  oppFrom: number;
  oppUntil: number;
}

/** Context lines to draw for an entry: all of ours, the opponent's only inside its window. */
export function visibleLines(entry: QueueEntry): ContextLine[] {
  return entry.item.clip.lines.filter(l => l.side === 1 || (l.t >= entry.oppFrom && l.t <= entry.oppUntil));
}

/** Player tracks to draw for an entry: teammates in full, opponents only inside its window. */
export function visibleTracks(entry: QueueEntry): Track[] {
  return entry.item.clip.tracks
    .map(tr => (tr.side === 1 ? tr : { ...tr, kf: tr.kf.filter(k => k.t >= entry.oppFrom && k.t <= entry.oppUntil) }))
    .filter(tr => tr.kf.length > 0);
}

/** No replay runs longer than this on screen (owner call 2026-09-30); longer ones play proportionally faster. */
export const REPLAY_LIMIT_SECONDS = 10;
/** Pauses the player holds on the last frame: after a single replay, and between clips. */
export const END_HOLD_SECONDS = 1.2;
export const BETWEEN_HOLD_SECONDS = 0.8;

/** Seconds of play an entry shows at normal speed (cut stoppages excluded). */
export function playSeconds(entry: Pick<QueueEntry, 'item' | 'startT' | 'endT'>): number {
  const cut = entry.item.clip.cuts.reduce((sum, [from, to]) => sum + Math.max(0, Math.min(entry.endT, to) - Math.max(entry.startT, from)), 0);
  return entry.endT - entry.startT - cut;
}

const rateToFit = (play: number, holds: number) => Math.max(1, play / (REPLAY_LIMIT_SECONDS - holds));

export function queueForMoment(items: ReplayItem[], key: string): QueueEntry[] {
  const queue: QueueEntry[] = [];
  for (const item of items) {
    const pop = item.pops.find(p => p.key === key);
    if (pop) queue.push({ item, startT: pop.anchor, endT: pop.end, focus: key, caption: null, rate: 1, oppFrom: pop.oppFrom, oppUntil: pop.oppUntil });
  }
  for (const item of items) {
    const cont = item.conts.find(c => c.key === key);
    if (cont) queue.push({ item, startT: cont.start, endT: cont.end, focus: key, caption: cont.caption, rate: 1, oppFrom: cont.oppFrom, oppUntil: cont.oppUntil });
  }
  // One speed for the whole replay (both clips), holds included in the limit.
  const holds = BETWEEN_HOLD_SECONDS * Math.max(0, queue.length - 1) + END_HOLD_SECONDS;
  const rate = rateToFit(queue.reduce((sum, entry) => sum + playSeconds(entry), 0), holds);
  return queue.map(entry => ({ ...entry, rate }));
}

export function queueForAll(items: ReplayItem[]): QueueEntry[] {
  // Play all: each clip, with the pause after it, fits the same limit.
  return items.map(item => {
    const entry = { item, startT: item.start, endT: item.end };
    return {
      ...entry,
      focus: null,
      caption: item.pops.length ? null : item.conts[0]?.caption ?? null,
      rate: rateToFit(playSeconds(entry), BETWEEN_HOLD_SECONDS),
      oppFrom: item.oppFrom,
      oppUntil: item.oppUntil,
    };
  });
}

/**
 * One playback step: which moments score by T, and which moment the badge
 * shows. In Play all the badge moves to the next moment just before it
 * scores, but only after the previous one has scored and shown for a beat.
 */
export function scoreStep(
  pops: Pop[],
  hits: ReadonlySet<string>,
  badgeKey: string | null,
  T: number,
  playAll: boolean,
): { badgeKey: string | null; scored: string[] } {
  let badge = badgeKey;
  const scored: string[] = [];
  const isHit = (key: string) => hits.has(key) || scored.includes(key);
  pops.forEach((pop, i) => {
    const prev = pops[i - 1];
    if (playAll && prev && isHit(prev.key) && !isHit(pop.key) && pop.key !== badge && T >= Math.max(pop.at - 2, prev.at + 1.2)) {
      badge = pop.key;
    }
    if (!isHit(pop.key) && T >= pop.at) {
      scored.push(pop.key);
      badge = pop.key;
    }
  });
  return { badgeKey: badge, scored };
}

// --- Sampling (per frame) --------------------------------------------------

export function ballAt(kf: Keyframe[], T: number): Point {
  if (!kf.length) return [60, 40];
  if (T <= kf[0].t) return [kf[0].x, kf[0].y];
  for (let i = 1; i < kf.length; i++) {
    if (T < kf[i].t) {
      const a = kf[i - 1];
      const b = kf[i];
      const u = (T - a.t) / ((b.t - a.t) || 1);
      return [a.x + (b.x - a.x) * u, a.y + (b.y - a.y) * u];
    }
  }
  const z = kf[kf.length - 1];
  return [z.x, z.y];
}

/** A player's position and opacity: shown around their touches, faded otherwise. */
export function playerAt(track: Track, T: number): { x: number; y: number; a: number } | null {
  let prev: Keyframe | null = null;
  let next: Keyframe | null = null;
  for (const p of track.kf) {
    if (p.t <= T) prev = p;
    else { next = p; break; }
  }
  if (prev && next && next.t - prev.t <= 10) {
    const u = (T - prev.t) / ((next.t - prev.t) || 1);
    return { x: prev.x + (next.x - prev.x) * u, y: prev.y + (next.y - prev.y) * u, a: 1 };
  }
  if (prev && T - prev.t <= 2.4) return { x: prev.x, y: prev.y, a: 1 - Math.max(0, (T - prev.t - 1.4) / 1.0) };
  if (next && next.t - T <= 0.7) return { x: next.x, y: next.y, a: 1 - (next.t - T) / 0.7 };
  return null;
}

/** Whether the featured player is on the ball at T (gold ring and label only then). */
export function featuredOnBall(clip: PreparedClip, playerId: number, T: number): boolean {
  let latest: ReplayEvent | null = null;
  for (const e of clip.ev) {
    if (e.t > T + 0.02) break;
    if (e.n !== 'dribble-past' && !e.ghost && !e.inCarry) latest = e;
  }
  if (!latest || latest.pid !== playerId) return false;
  if (GAIN.includes(latest.n)) return true;
  return T - latest.t < 0.4;
}

/** The player who last touched the ball (within 2.5 s); teammates are named while they have it. */
export function holderAt(clip: PreparedClip, T: number): number | null {
  let holder: number | null = null;
  for (const e of clip.ev) {
    if (e.t > T + 0.02) break;
    if (e.pid != null && T - e.t <= 2.5 && e.n !== 'dribble-past' && !e.ghost && !e.inCarry) holder = e.pid;
  }
  return holder;
}

export interface LabelSpot {
  anchor: 'start' | 'middle' | 'end';
  dx: number;
  below: boolean;
  /** No room at a touchline: this teammate's name waits (the player's own never hides). */
  hidden: boolean;
}

/** Approximate rendered width of a name label in pitch units. */
export const labelWidth = (text: string, star: boolean): number => text.length * (star ? 3.4 : 2.9) * 0.55;

/** Label baseline offsets from the dot (above / below) and text height, in pitch units. */
export const labelOffset = (star: boolean, below: boolean): number => (below ? (star ? 5.4 : 4.6) : (star ? -3.6 : -2.8));
const labelHeight = (star: boolean) => (star ? 3.4 : 2.9) * 0.8;

/**
 * Where each name label sits: hung inward near a touchline or corner (the SVG
 * clips at the pitch edge), under the dot near the top edge, and when two
 * names would collide the lower player's label goes under its dot. If a
 * touchline leaves no room for both, the teammate's name waits.
 */
export function labelSpots(players: { x: number; y: number; labeled: boolean; w?: number; star?: boolean }[]): LabelSpot[] {
  const spots: LabelSpot[] = players.map(p => {
    const half = (p.w ?? 12) / 2;
    const anchor = p.x + half > 121 ? 'end' : p.x - half < -1 ? 'start' : 'middle';
    return { anchor, dx: anchor === 'end' ? 2 : anchor === 'start' ? -2 : 0, below: p.y < 6, hidden: false };
  });
  const box = (i: number): [number, number, number, number] => {
    const w = players[i].w ?? 12;
    const star = !!players[i].star;
    const x = players[i].x + spots[i].dx;
    const [x0, x1] = spots[i].anchor === 'end' ? [x - w, x] : spots[i].anchor === 'start' ? [x, x + w] : [x - w / 2, x + w / 2];
    const base = players[i].y + labelOffset(star, spots[i].below);
    return [x0, base - labelHeight(star), x1, base];
  };
  const collide = (i: number, j: number) => {
    const a = box(i);
    const b = box(j);
    return a[0] < b[2] + 1 && b[0] < a[2] + 1 && a[1] < b[3] && b[1] < a[3];
  };
  const shown = players.map((_, i) => i).filter(i => players[i].labeled);
  for (let a = 0; a < shown.length; a++) {
    for (let b = a + 1; b < shown.length; b++) {
      const i = shown[a];
      const j = shown[b];
      if (spots[i].hidden || spots[j].hidden || !collide(i, j)) continue;
      const lower = players[i].y >= players[j].y ? i : j;
      const upper = lower === i ? j : i;
      // Near the bottom line there is no room under the dot; near the top, none above.
      spots[lower].below = players[lower].y <= 76;
      spots[upper].below = players[upper].y < 6;
      if (collide(i, j)) {
        const teammate = players[i].star ? j : players[j].star ? i : lower;
        spots[teammate].hidden = true;
      }
    }
  }
  return spots;
}

/** How far along its line a mark is drawn at T (0..1). */
export function markProgress(mark: Mark, T: number): number {
  if (T < mark.t0) return -1;
  return mark.t1 > mark.t0 ? clamp((T - mark.t0) / (mark.t1 - mark.t0), 0, 1) : 1;
}
